import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Paper,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  Fab,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Layers as LayersIcon,
  FilterList as FilterIcon,
  Timeline as AnalysisIcon,
  BarChart as StatsIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import { toast } from 'react-toastify';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

// Import service
import geospatialService from '../../services/geospatialService';

// Import sub-components
import FilterPanel from './FilterPanel';
import LayerControl from './LayerControl';
import AnalysisTools from './AnalysisTools';
import StatisticsPanel from './StatisticsPanel';
import MapLayers from './MapLayers';

const DRAWER_WIDTH = 450;

const GISDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Drawer states - Start with closed for full-screen experience
  const [leftDrawerOpen, setLeftDrawerOpen] = useState(false);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
  const [activePanel, setActivePanel] = useState('filters'); // filters, layers, analysis, stats
  
  // Map state
  const [map, setMap] = useState(null);
  const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]); // India center
  const [mapZoom, setMapZoom] = useState(5);
  
  // Data state
  const [geoData, setGeoData] = useState(null);
  const [heatmapData, setHeatmapData] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    status: '',
    damageType: '',
    startDate: null,
    endDate: null,
    district: ''
  });
  
  // Layer visibility state
  const [layers, setLayers] = useState({
    markers: true,
    clusters: true,
    heatmap: true,
    boundaries: false
  });
  
  // Analysis state
  const [analysisMode, setAnalysisMode] = useState(null); // buffer, distance, null
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Handle panel toggle
  const handlePanelToggle = (panel) => {
    if (activePanel === panel) {
      setLeftDrawerOpen(!leftDrawerOpen);
    } else {
      setActivePanel(panel);
      setLeftDrawerOpen(true);
    }
  };
  
  // Fetch data based on filters
  const fetchGeoData = async () => {
    setLoading(true);
    try {
      const params = {
        status: filters.status,
        damage_type: filters.damageType,
        start_date: filters.startDate,
        end_date: filters.endDate,
      };
      
      // Fetch cluster data
      const data = await geospatialService.getClusterData(params);
      setGeoData(data);
      
      // Fetch heatmap data if heatmap layer is active
      if (layers.heatmap) {
        const heatData = await geospatialService.getHeatmapData(params);
        setHeatmapData(heatData.points || []);
      }
      
      // Fetch statistics
      const stats = await geospatialService.getStatistics(params);
      setStatistics(stats);
      
    } catch (error) {
      console.error('Error fetching geospatial data:', error);
      toast.error('Failed to load geospatial data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Initial data load
  useEffect(() => {
    fetchGeoData();
  }, [filters, layers.heatmap]);
  
  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };
  
  // Handle layer toggle
  const handleLayerToggle = (layerName) => {
    setLayers(prev => ({
      ...prev,
      [layerName]: !prev[layerName]
    }));
  };
  
  // Render side panel content
  const renderPanelContent = () => {
    switch (activePanel) {
      case 'filters':
        return (
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onApply={fetchGeoData}
            loading={loading}
          />
        );
      case 'layers':
        return (
          <LayerControl
            layers={layers}
            onLayerToggle={handleLayerToggle}
          />
        );
      case 'analysis':
        return (
          <AnalysisTools
            map={map}
            analysisMode={analysisMode}
            setAnalysisMode={setAnalysisMode}
            analysisResult={analysisResult}
            setAnalysisResult={setAnalysisResult}
          />
        );
      case 'stats':
        return (
          <StatisticsPanel
            statistics={statistics}
            geoData={geoData}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };
  
  return (
    <Box>
      {/* Floating Side Panel */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={leftDrawerOpen}
        onClose={() => {}}
        hideBackdrop={true}
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            top: isMobile ? 0 : '64px',
            height: isMobile ? '100%' : 'calc(100% - 64px)',
            zIndex: 1200,
            boxShadow: theme.shadows[8],
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            pointerEvents: 'auto',
          },
        }}
        ModalProps={{
          keepMounted: true,
          disablePortal: false,
          style: { pointerEvents: 'none' },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {activePanel === 'filters' && 'Filters'}
            {activePanel === 'layers' && 'Map Layers'}
            {activePanel === 'analysis' && 'Analysis Tools'}
            {activePanel === 'stats' && 'Statistics'}
          </Typography>
          <IconButton onClick={() => setLeftDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
          {renderPanelContent()}
        </Box>
      </Drawer>
      
      {/* Full Screen Map Area */}
      <Box
        component="main"
        sx={{
          position: 'absolute',
          top: isMobile ? 0 : '64px',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: isMobile ? '100vh' : 'calc(100vh - 64px)',
        }}
      >
        {/* Map Container */}
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          whenCreated={setMap}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Map Layers Component */}
          <MapLayers
            geoData={geoData}
            heatmapData={heatmapData}
            layers={layers}
            analysisMode={analysisMode}
            setAnalysisResult={setAnalysisResult}
          />
        </MapContainer>
        
        {/* Floating Control Panel - Top Left */}
        <Paper
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1000,
            display: 'flex',
            gap: 1,
            p: 1,
            borderRadius: 2,
            boxShadow: theme.shadows[4],
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Tooltip title="Filters" placement="bottom">
            <IconButton
              size="medium"
              color={activePanel === 'filters' && leftDrawerOpen ? 'primary' : 'default'}
              onClick={() => handlePanelToggle('filters')}
              sx={{
                backgroundColor: activePanel === 'filters' && leftDrawerOpen 
                  ? theme.palette.primary.light 
                  : 'transparent',
              }}
            >
              <FilterIcon />
            </IconButton>
          </Tooltip>
          
          <Divider orientation="vertical" flexItem />
          
          <Tooltip title="Layers" placement="bottom">
            <IconButton
              size="medium"
              color={activePanel === 'layers' && leftDrawerOpen ? 'primary' : 'default'}
              onClick={() => handlePanelToggle('layers')}
              sx={{
                backgroundColor: activePanel === 'layers' && leftDrawerOpen 
                  ? theme.palette.primary.light 
                  : 'transparent',
              }}
            >
              <LayersIcon />
            </IconButton>
          </Tooltip>
          
          <Divider orientation="vertical" flexItem />
          
          <Tooltip title="Analysis Tools" placement="bottom">
            <IconButton
              size="medium"
              color={activePanel === 'analysis' && leftDrawerOpen ? 'primary' : 'default'}
              onClick={() => handlePanelToggle('analysis')}
              sx={{
                backgroundColor: activePanel === 'analysis' && leftDrawerOpen 
                  ? theme.palette.primary.light 
                  : 'transparent',
              }}
            >
              <AnalysisIcon />
            </IconButton>
          </Tooltip>
          
          <Divider orientation="vertical" flexItem />
          
          <Tooltip title="Statistics" placement="bottom">
            <Badge
              badgeContent={statistics?.total_locations || 0}
              color="error"
              max={999}
            >
              <IconButton
                size="medium"
                color={activePanel === 'stats' && leftDrawerOpen ? 'primary' : 'default'}
                onClick={() => handlePanelToggle('stats')}
                sx={{
                  backgroundColor: activePanel === 'stats' && leftDrawerOpen 
                    ? theme.palette.primary.light 
                    : 'transparent',
                }}
              >
                <StatsIcon />
              </IconButton>
            </Badge>
          </Tooltip>
        </Paper>
        
        {/* Info Card - Bottom Left */}
        <Paper
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            zIndex: 1000,
            p: 1.5,
            borderRadius: 2,
            boxShadow: theme.shadows[4],
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(10px)',
            minWidth: 200,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: loading ? theme.palette.warning.main : theme.palette.success.main,
                animation: loading ? 'pulse 1.5s ease-in-out infinite' : 'none',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                },
              }}
            />
            <Typography variant="body2" fontWeight="medium">
              {loading ? 'Loading...' : `${geoData?.features?.length || 0} Locations`}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default GISDashboard;
